import { Box, Card, Grid, Stack, Typography, useTheme } from "@mui/material";
import { useTranslation } from "react-i18next";
import TitleComponent from '../../components/widgets/titleComponent';
import careerData from '../../assets/data/careers/careers.json';
import companyData from '../../assets/data/company.json';
import PaginationComponent from "../../components/widgets/paginationComponent";
import { useCallback, useState } from "react";
import Brightness1Icon from '@mui/icons-material/Brightness1';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import { Link } from "react-router-dom";

const ITEMS_PER_PAGE = 5;

const JobOpenings = () => {
    const theme = useTheme();
    const {t} = useTranslation();

    const [activePage, setActivePage] = useState(0);
    
    const changePage = useCallback((page) => {
        setActivePage(page);
    }, []);

    return (
        <>
            <Box 
                className="sectionSpacing"
                width='100%'
                sx={{
                    backgroundColor: theme.palette.background.default
                }}
            >
                <Stack
                    direction='column'
                    justifyContent='center'
                    alignItems='center'
                    px='5%'
                >
                    <TitleComponent 
                        title={t('careers.jobOpenings.title')}
                        description={t('careers.jobOpenings.description')}
                    />

                    {
                        careerData.jobOpenings.length > 0
                        ?
                        <Stack
                            direction='column'
                            justifyItems='start'
                            alignItems='center'
                            width='100%'
                            spacing={5}
                        >
                            <Stack
                                direction='column'
                                justifyItems='start'
                                alignItems='center'
                                width='100%'
                                spacing={3}
                            >
                                {
                                    careerData.jobOpenings.slice(activePage * ITEMS_PER_PAGE, ITEMS_PER_PAGE * (1 + activePage)).map((item, index) => {
                                        return <JobOpeningTile 
                                            key={index}
                                            data={item}
                                        />
                                    })
                                }
                            </Stack>

                            {/* Pagination */}
                            <PaginationComponent 
                                totalItems={careerData.jobOpenings.length}
                                itemsPerPage={ITEMS_PER_PAGE}
                                activePage={activePage}
                                onPageChange={changePage}
                            />
                        </Stack>
                        :
                        <Typography
                            fontWeight={500}
                            fontSize={{xs: 14, md: 16}}
                            textAlign='center'
                            color={theme.palette.text.secondary}
                            lineHeight={1.5}
                            pb={{xs: 0, md: 5}}
                        >
                            {t('careers.jobOpenings.noJobs')}
                        </Typography>
                    }

                    <Stack
                        direction='column'
                        justifyItems='start'
                        alignItems='center'
                        width='100%'
                        spacing={{xs: 0.5, md: 1}}
                        pt={careerData.jobOpenings.length > 0 ? 2 : 5}
                    >
                        <Typography
                            fontWeight={700}
                            fontSize={{xs: 24, md: 50}}
                            textAlign='center'
                            color={theme.palette.text.primary}
                            lineHeight={1.5}
                            pb={{xs: 1, md: 3}}
                        >
                            {t('careers.jobOpenings.noRole')}
                        </Typography>

                        <Typography
                            fontWeight={500}
                            fontSize={{xs: 14, md: 24}}
                            textAlign='center'
                            color={theme.palette.text.secondary}
                            lineHeight={1.5}
                        >
                            {t('careers.jobOpenings.shareCV')}
                        </Typography>

                        <Link
                            to={`mailto:${companyData.contact.careers_email}`}
                            style={{
                                textDecoration: 'none'
                            }}
                        >
                            <Typography
                                fontWeight={600}
                                fontSize={{xs: 14, md: 24}}
                                textAlign='center'
                                color={theme.palette.text.primary}
                                lineHeight={1.5}
                                sx={{
                                    '&: hover': {
                                        transform: 'scale(1.1)'
                                    }
                                }}
                            >
                                {companyData.contact.careers_email}
                            </Typography>
                        </Link>
                    </Stack>
                </Stack>
            </Box>
        </>
    );
}

export default JobOpenings;

const JobOpeningTile = ({data}) => {
    const theme = useTheme();

    return (
        <>
            <Link
                to={`/careers/${data.file}`}
                style={{
                    width: '100%',
                    textDecoration: 'none'
                }}
                data-aos="slide-up"
            >
                <Card
                    elevation={3}
                    sx={{
                        width: '100%',
                        backgroundColor: theme.palette.background.default,
                        borderRadius: 4,
                        '&: hover': {
                            transform: 'scale(1.01)'
                        }
                    }}
                >
                    <Grid
                        direction='row'
                        justifyContent='start'
                        alignItems='center'
                        container
                        p={{xs: 3, md: 4}}
                        spacing={1}
                    >
                        <Grid size={{xs: 12, md: 5}}>
                            <Typography
                                fontWeight={600}
                                fontSize={{xs: 16, md: 24}}
                                textAlign='left'
                                color={theme.palette.text.primary}
                                lineHeight={1.5}
                            >
                                {data.title}
                            </Typography>
                        </Grid>

                        <Grid size={{xs: 5, md: 3}}>
                            <Typography
                                fontWeight={500}
                                fontSize={{xs: 12, md: 12, lg: 14}}
                                textAlign="left"
                                lineHeight={1.5}
                                color={theme.palette.text.secondary}
                            >
                                <span>{<Brightness1Icon sx={{ fontSize: 10, pr: 1 }} />}</span>
                                {data.location}
                            </Typography>
                        </Grid>

                        <Grid size={{xs: 5, md: 3}}>
                            <Typography
                                fontWeight={500}
                                fontSize={{xs: 12, md: 12, lg: 14}}
                                textAlign="left"
                                lineHeight={1.5}
                                color={theme.palette.text.secondary}
                            >
                                <span>{<Brightness1Icon sx={{ fontSize: 10, pr: 1 }} />}</span>
                                {data.employment}
                            </Typography>
                        </Grid>

                        <Grid 
                            size={{xs: 2, md: 1}}
                            display='flex'
                            direction='row'
                            justifyContent='end'
                        >
                            <ArrowForwardIosIcon sx={{ fontSize: 20 }} />
                        </Grid>
                    </Grid>
                </Card>
            </Link>
        </>
    );
}
